// ===========================================================================
// The rooms that are always open.
//
// A directory of user-made rooms is empty at 4 a.m. and empty on a quiet
// Tuesday, and an empty directory tells a new visitor there is nobody here.
// Standing rooms are the fix: system-owned rooms that exist whether or not
// anyone is in them, so "the quiet room" and "practice Spanish" are places
// with a door, not a search that comes back with nothing (C4 and E1 in the
// September contract; the retention evidence is in research 06, section 6.2).
//
// This module is the CATALOG only -- what the rooms are called, which band
// gets them, and the rules card each one pins. Creating them, counting heads,
// and the admin on/off switch live in the server (GroupRooms.ensureStanding).
//
// Every rules card is written for a 13-year-old, because both bands read the
// same one. Short lines, plain words, one idea per line. The default card is
// what every OTHER room gets pinned (C5). ASCII only, American English.
// ===========================================================================

/** The shared timer lengths in the quiet room, in minutes (C4). */
export const FOCUS_MINUTES = Object.freeze([25, 50]);

/** The one-line "what I'm doing" status in the quiet room, max characters. */
export const ROOM_STATUS_MAX = 60;

/**
 * The rules card pinned in every room that does not have its own. The order
 * is deliberate: the two things you must do, the one thing you must not, then
 * what happens when something goes wrong, then the exit -- so the last line a
 * nervous newcomer reads is that they can leave.
 */
export const DEFAULT_RULES = Object.freeze([
  'Be kind. Everyone here is a real person.',
  'Keep it clean. No nudity, no dirty talk, nothing violent, on camera or in words.',
  'No personal details: not your own, and not anyone else\'s.',
  'Report anything that feels wrong. You do not need to be sure.',
  'A real person reviews every report.',
  'Leave whenever you like. Nobody will ask why.',
]);

const BOTH = Object.freeze(['adult', 'minor']);
const ADULTS = Object.freeze(['adult']);

const REPORT_LINE = 'Report anything that feels wrong. A real person reviews every report.';
const DETAILS_LINE = 'No personal details, yours or anyone else\'s.';

/** A language-practice room. The language name is what a learner sees. */
function practice(slug, lang, language) {
  return {
    slug,
    name: `Practice ${language}`,
    mode: 'audio',
    lang,
    quiet: false,
    bands: BOTH,
    rules: Object.freeze([
      `Speak ${language} as much as you can. Mistakes are the point.`,
      'Be patient. Somebody here is at the level you were once.',
      'Correct gently, and only when someone asks.',
      `${DETAILS_LINE} Keep it about the words.`,
      REPORT_LINE,
      'Leave whenever you like.',
    ]),
    blurb: `Practice speaking ${language} with people who are learning too.`,
    page: 'practice-a-language',
  };
}

/** A night-owls room. The region is a hint about the hour, not a filter. */
function nightOwls(slug, region, blurb) {
  return {
    slug,
    name: `Night Owls: ${region}`,
    mode: 'audio',
    lang: null,
    quiet: false,
    bands: BOTH,
    rules: Object.freeze([
      'Late where you are, early somewhere else. Everyone is welcome at any hour.',
      'Be kind. Tired people deserve gentle company.',
      'Keep it clean: no nudity, nothing violent, on camera or in words.',
      `${DETAILS_LINE} No need to say where you are.`,
      REPORT_LINE,
      'Leave whenever you like, even mid-sentence.',
    ]),
    blurb,
    page: 'night-owls',
  };
}

export const STANDING_ROOMS = Object.freeze([
  {
    slug: 'quiet-room',
    name: 'The Quiet Room',
    mode: 'audio',
    lang: null,
    quiet: true,
    bands: BOTH,
    rules: Object.freeze([
      'Mics start muted and cameras start off. Turn either on if you want to.',
      'Say what you are working on in your status line, or say nothing at all.',
      'Anyone can start the shared timer when none is running. Rest when it ends.',
      'Keep chat quiet: a hello and a goodbye are plenty.',
      `${DETAILS_LINE} Report anything that feels wrong.`,
      'Leave whenever you like. Nobody will ask why.',
    ]),
    blurb: 'Work, read, or rest alongside other people, with mics muted and a shared timer.',
    page: 'quiet-room',
  },
  practice('practice-english', 'en', 'English'),
  practice('practice-spanish', 'es', 'Spanish'),
  practice('practice-french', 'fr', 'French'),
  practice('practice-german', 'de', 'German'),
  practice('practice-portuguese', 'pt', 'Portuguese'),
  practice('practice-italian', 'it', 'Italian'),
  practice('practice-dutch', 'nl', 'Dutch'),
  nightOwls('night-owls-americas', 'Americas', 'Company for the small hours, for anyone awake in the Americas or anywhere else.'),
  nightOwls('night-owls-europe-africa', 'Europe and Africa', 'Company for the small hours across Europe and Africa, and anyone else awake.'),
  nightOwls('night-owls-asia-pacific', 'Asia and Pacific', 'Company for the small hours across Asia and the Pacific, and anyone else awake.'),
  {
    slug: 'new-in-a-country',
    name: 'New in a Country',
    mode: 'audio',
    lang: null,
    quiet: false,
    bands: BOTH,
    rules: Object.freeze([
      'Everyone here is new somewhere, or remembers being new. Ask anything.',
      'Be kind about accents and mistakes, yours and everyone else\'s.',
      'Share what has helped you settle in.',
      `${DETAILS_LINE} Where you live is yours to keep.`,
      REPORT_LINE,
      'Leave whenever you like.',
    ]),
    blurb: 'Casual conversation for anyone settling into a new country.',
    page: 'new-in-a-country',
  },
  {
    slug: 'carers',
    name: 'Carers',
    mode: 'audio',
    lang: null,
    quiet: false,
    bands: ADULTS,
    rules: Object.freeze([
      'Drop in for ten minutes or stay an hour. Nothing here needs scheduling.',
      'Be kind. Everyone here is carrying something.',
      'Listen as much as you talk. Advice only when someone asks for it.',
      'No personal details about you or the person you care for.',
      REPORT_LINE,
      'Leave whenever you need to. Nobody will ask why.',
    ]),
    blurb: 'Ten minutes of adult conversation for people who look after someone.',
    page: 'carers',
  },
].map((room) => Object.freeze(room)));

const BY_SLUG = new Map(STANDING_ROOMS.map((r) => [r.slug, r]));

/** The catalog entry for a slug, or null. */
export function standingRoom(slug) {
  return BY_SLUG.get(String(slug || '')) || null;
}

/**
 * The rooms a band gets. An unknown band gets the minor set, which is the
 * smaller one -- the same rule as everywhere else: when in doubt, the
 * 13-year-old's version.
 */
export function standingRoomsFor(band) {
  const b = band === 'adult' ? 'adult' : 'minor';
  return STANDING_ROOMS.filter((r) => r.bands.includes(b));
}

export default { STANDING_ROOMS, DEFAULT_RULES, FOCUS_MINUTES, ROOM_STATUS_MAX, standingRoom, standingRoomsFor };
